import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { ThemeSelector } from "./ThemeSelector";
import { AnimatedButton } from "./ui/AnimatedButton";

interface ClientPageHeaderProps {
  onBack: () => void;
}

/**
 * The page header of a client's pages: "‹ Back", the product's name, the
 * theme and Log out. Shared by the client page and the numbers page so the
 * Back button sits on the header's colours on both (walk-through round 1,
 * finding 11).
 */
export function ClientPageHeader({ onBack }: ClientPageHeaderProps): ReactNode {
  const { logout } = useAuth();
  return (
    <header>
      <div className="client-detail-header">
        <AnimatedButton type="button" className="back-button" onClick={onBack}>
          &#8249; Back
        </AnimatedButton>
        <h1>prime</h1>
      </div>
      <div className="user-info">
        <ThemeSelector />
        <button type="button" onClick={() => void logout()}>
          Log out
        </button>
      </div>
    </header>
  );
}

interface ClientBreadcrumbProps {
  clientName: string;
  /** The client page's address when this page is below it; the name then links there. */
  clientPath?: string;
  /** The label of the page below the client page ("Numbers"), if any. */
  current?: string;
}

/** "Your businesses / <client's name>[ / <page>]": the client's name on every client page. */
export function ClientBreadcrumb({
  clientName,
  clientPath,
  current,
}: ClientBreadcrumbProps): ReactNode {
  const separator = (
    <span className="breadcrumb-separator" aria-hidden="true">
      /
    </span>
  );
  return (
    <nav className="breadcrumb">
      <Link to="/clients">Your businesses</Link>
      {separator}
      {current && clientPath ? (
        <>
          <Link to={clientPath}>{clientName}</Link>
          {separator}
          <span className="breadcrumb-current">{current}</span>
        </>
      ) : (
        <span className="breadcrumb-current">{clientName}</span>
      )}
    </nav>
  );
}
